import type { ReactNode } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog'
import { LevelBadge, StatusBadge } from '../../../components/common/Status'
import { TimeAgo } from '../../../components/common/TimeAgo'
import { eventTypeLabel, eventStatusLabel } from '../../../lib/alert-level'
import { formatAbsTs, formatCoord, formatMag } from '../../../lib/format'
import type { Event } from '../../../api/schemas'

/** 事件快速视图弹窗：header/footer 由调用方扩展（footer 放跳转按钮） */
export function EventDetailDialog({ event, onClose, children, detail }: {
  event: Event | null
  onClose: () => void
  children?: ReactNode
  detail?: ReactNode
}) {
  const navigate = useNavigate()
  return (
    <Dialog open={Boolean(event)} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-xl">
        {event && (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2">
                <LevelBadge alertLevel={event.alertLevel} severity={event.severity} pulse={event.status === 'pending'} />
                <StatusBadge status={event.status} labels={eventStatusLabel} />
                <span className="text-[10px] text-muted-foreground">{eventTypeLabel(event.type)}</span>
              </div>
              <DialogTitle className="mt-2">{event.name || event.eventId}</DialogTitle>
              <DialogDescription>
                <TimeAgo ts={event.originTime} /> · {formatAbsTs(event.originTime)}
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-4 gap-2 text-center">
              <Cell label="震级" value={formatMag(event.magnitude)} tone="text-critical" />
              <Cell label="深度" value={`${event.depth.toFixed(0)} km`} />
              <Cell label="触发/确认" value={`${event.triggerCount}/${event.confirmedDevices}`} />
              <Cell label="预估到达" value={event.estimatedArrivalSec > 0 ? `${event.estimatedArrivalSec}s` : '—'} tone="text-warning" />
            </div>

            <div className="mt-3 flex items-center justify-between rounded-lg bg-black/20 px-3 py-2">
              <span className="font-mono text-[11px] text-muted-foreground">{formatCoord(event.lat, event.lon)}</span>
              <span className="font-mono text-[10px] text-muted-foreground/60">{event.geohash}</span>
            </div>

            {detail}

            <DialogFooter>
              {children}
              <button
                onClick={() => void navigate(`/events/${event.eventId}`)}
                className="flex items-center gap-1 text-xs text-primary hover:underline"
              >
                聚合详情 <ArrowUpRight size={12} />
              </button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

function Cell({ label, value, tone = 'text-foreground' }: { label: string; value: string; tone?: string }) {
  return (
    <div className="rounded-lg bg-black/20 py-2">
      <div className="text-[10px] text-muted-foreground">{label}</div>
      <div className={`mt-0.5 font-mono text-sm font-semibold tabular-nums ${tone}`}>{value}</div>
    </div>
  )
}
